import type { AccountTypeRecord, LenderParentRecord } from '@/shared/api/services/lenderService';

export const normalizeString = (value: unknown): string => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

export const isAffirmative = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1';
  }
  return false;
};

export const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.prototype.toString.call(value) === '[object Object]'
);

export const findLenderByName = (
  lookup: Map<string, LenderParentRecord>,
  lendersList: LenderParentRecord[],
  name: string | undefined
): LenderParentRecord | undefined => {
  if (!name) {
    return undefined;
  }
  const normalized = normalizeString(name);
  const trimmedOriginal = typeof name === 'string' ? name.trim().toLowerCase() : '';
  const fromLookup = lookup.get(normalized);
  if (fromLookup) {
    return fromLookup;
  }

  for (const record of lendersList) {
    if (!record) continue;
    const parentKeyParts = new Set<string>();
    const parentCompanyKey = normalizeString(record.parentCompany);
    if (parentCompanyKey) parentKeyParts.add(parentCompanyKey);
    const descriptionKey = normalizeString(record.description);
    if (descriptionKey) parentKeyParts.add(descriptionKey);
    const parentRaw = record.raw as { parentCompany?: string; lender?: string } | undefined;
    const parentRootKey = normalizeString(parentRaw?.parentCompany);
    if (parentRootKey) {
      parentKeyParts.add(parentRootKey);
    }
    const lenderKey = normalizeString((record as { lender?: string }).lender);
    if (lenderKey) {
      parentKeyParts.add(lenderKey);
    }

    const childCompanies = Array.isArray(record.childCompany) ? record.childCompany : [];
    for (const childName of childCompanies) {
      const childNormalized = normalizeString(childName);
      if (!childNormalized) {
        continue;
      }
      if (childNormalized === normalized || childNormalized === trimmedOriginal) {
        return record;
      }
      for (const parentKey of parentKeyParts) {
        if (!parentKey) {
          continue;
        }
        const compositeKey = `${parentKey}|${childNormalized}`;
        if (compositeKey === normalized) {
          return record;
        }
        const reverseCompositeKey = `${childNormalized}|${parentKey}`;
        if (reverseCompositeKey === normalized) {
          return record;
        }
      }
    }

    for (const parentKey of parentKeyParts) {
      if (!parentKey) continue;
      if (parentKey === normalized || parentKey === trimmedOriginal) {
        return record;
      }
    }
  }

  return undefined;
};

export const buildAccountTypeOptions = (records: AccountTypeRecord[]): Array<{ value: string; label: string }> => {
  const map = new Map<string, { value: string; label: string }>();
  records.forEach((item) => {
    const code = typeof item.code === 'string' ? item.code.trim() : '';
    if (!code) {
      return;
    }
    const label = (item.label ?? item.code)?.trim() || code;
    if (!map.has(code)) {
      map.set(code, { value: code, label });
    }
  });
  return Array.from(map.values());
};

